import React, { useEffect, useState, useMemo, useRef } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fetchJson = async (url, opts = {}) => {
  const r = await fetch(`${BACKEND}${url}`, opts);
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
  return data;
};

const AdminRetell = () => {
  const [agents, setAgents] = useState([]);
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [showCustomOnly, setShowCustomOnly] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [previewingId, setPreviewingId] = useState("");

  // Test chiamata
  const [testTo, setTestTo] = useState("+39");
  const [testFrom, setTestFrom] = useState("");
  const [callMsg, setCallMsg] = useState("");
  const [callBusy, setCallBusy] = useState(false);

  const previewAudioRef = useRef(null);

  const reload = async () => {
    setLoading(true);
    setError("");
    try {
      const [vRes, aRes] = await Promise.all([
        fetchJson(`/api/retell/voices?provider=cartesia&include_custom=true`),
        fetchJson(`/api/retell/agents`),
      ]);
      setVoices(vRes.voices || []);
      setAgents(aRes.agents || []);
      // Preseleziono primo agente con numero (utile per testare)
      const withNum = (aRes.agents || []).find((a) => (a.phone_numbers || []).length > 0);
      setSelectedAgentId(withNum?.agent_id || (aRes.agents?.[0]?.agent_id || ""));
      if (withNum?.phone_numbers?.[0]?.phone_number) {
        setTestFrom(withNum.phone_numbers[0].phone_number);
      }
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const selectedAgent = useMemo(
    () => agents.find((a) => a.agent_id === selectedAgentId),
    [agents, selectedAgentId]
  );

  const filteredVoices = useMemo(() => {
    let list = voices;
    if (showCustomOnly) list = list.filter((v) => v.is_custom);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((v) =>
        `${v.voice_name} ${v.voice_id} ${v.accent || ""} ${v.gender || ""}`.toLowerCase().includes(s)
      );
    }
    return list;
  }, [voices, search, showCustomOnly]);

  const handlePreview = (v) => {
    try {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current = null;
      }
      if (!v.preview_audio_url) {
        alert("Nessun preview disponibile per questa voce.");
        return;
      }
      const audio = new Audio(v.preview_audio_url);
      previewAudioRef.current = audio;
      setPreviewingId(v.voice_id);
      audio.onended = () => setPreviewingId("");
      audio.onerror = () => setPreviewingId("");
      audio.play().catch(() => setPreviewingId(""));
    } catch (e) {
      console.warn(e);
      setPreviewingId("");
    }
  };

  const handleAssignVoice = async (voice) => {
    if (!selectedAgent) {
      alert("Seleziona prima un agente dalla colonna sinistra.");
      return;
    }
    const ok = window.confirm(
      `Assegnare la voce "${voice.voice_name}" all'agente "${selectedAgent.agent_name}" con lingua it-IT?`
    );
    if (!ok) return;
    setSaving(true);
    setSavedMsg("");
    try {
      const body = {
        voice_id: voice.voice_id,
        language: "it-IT",
        // voice_model: solo per Cartesia preset, lasciamo che Retell decida
      };
      await fetchJson(`/api/retell/agents/${selectedAgent.agent_id}/voice`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setSavedMsg(`✓ Voce "${voice.voice_name}" assegnata a "${selectedAgent.agent_name}" (it-IT)`);
      await reload();
    } catch (e) {
      setSavedMsg(`✗ Errore: ${e.message || e}`);
    } finally {
      setSaving(false);
      setTimeout(() => setSavedMsg(""), 8000);
    }
  };

  const handleOutboundCall = async () => {
    if (!testFrom || !testTo) {
      alert("Inserisci 'da' (numero Retell) e 'a' (numero italiano).");
      return;
    }
    setCallBusy(true);
    setCallMsg("");
    try {
      const body = { from_number: testFrom, to_number: testTo };
      if (selectedAgent?.agent_id) body.override_agent_id = selectedAgent.agent_id;
      const res = await fetchJson(`/api/retell/calls/outbound`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setCallMsg(`✓ Chiamata avviata. call_id=${res.call_id || "?"}`);
    } catch (e) {
      setCallMsg(`✗ ${e.message || e}`);
    } finally {
      setCallBusy(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Retell AI · Voci Cartesia</h1>
          <p style={styles.subtitle}>
            Assegna una voce Cartesia a un agente Retell e imposta la lingua a <code>it-IT</code>.
            Le voci preset Cartesia (accenti USA/UK/etc) parlano italiano automaticamente grazie al modello multilingua.
          </p>
        </div>
        <button onClick={reload} style={styles.btnSecondary} disabled={loading}>
          {loading ? "Carico..." : "↻ Ricarica"}
        </button>
      </div>

      {error && <div style={styles.errorBox}>⚠️ {error}</div>}
      {savedMsg && (
        <div style={savedMsg.startsWith("✓") ? styles.okBox : styles.errorBox}>{savedMsg}</div>
      )}

      <div style={styles.grid}>
        {/* AGENTI */}
        <div style={styles.col}>
          <h2 style={styles.colTitle}>Agenti ({agents.length})</h2>
          <div style={styles.list}>
            {agents.map((a) => {
              const isSelected = a.agent_id === selectedAgentId;
              const currentVoice = voices.find((v) => v.voice_id === a.voice_id);
              return (
                <div
                  key={a.agent_id}
                  onClick={() => setSelectedAgentId(a.agent_id)}
                  style={{
                    ...styles.agentCard,
                    ...(isSelected ? styles.agentCardSelected : {}),
                  }}
                >
                  <div style={styles.agentName}>{a.agent_name}</div>
                  <div style={styles.agentMeta}>
                    <span style={styles.tag}>
                      🎤 {currentVoice?.voice_name || a.voice_id || "—"}
                    </span>
                    {a.language && <span style={styles.tag}>🌐 {a.language}</span>}
                  </div>
                  {(a.phone_numbers || []).length > 0 && (
                    <div style={styles.phoneList}>
                      {a.phone_numbers.map((p, i) => (
                        <span key={i} style={styles.phoneTag}>
                          📞 {p.phone_number} {p.inbound && "↓"} {p.outbound && "↑"}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* TEST CHIAMATA OUTBOUND */}
          <div style={styles.testBox}>
            <h3 style={styles.testTitle}>📞 Test chiamata outbound</h3>
            <label style={styles.label}>Da (numero Retell):</label>
            <input
              type="text"
              value={testFrom}
              onChange={(e) => setTestFrom(e.target.value)}
              placeholder="+1681..."
              style={styles.input}
            />
            <label style={styles.label}>A (numero italiano):</label>
            <input
              type="text"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="+39328..."
              style={styles.input}
            />
            <div style={styles.testHint}>
              Agente: <b>{selectedAgent?.agent_name || "—"}</b>
            </div>
            <button
              onClick={handleOutboundCall}
              disabled={callBusy || !selectedAgent}
              style={styles.btnPrimary}
            >
              {callBusy ? "..." : "Chiama ora"}
            </button>
            {callMsg && <div style={styles.testMsg}>{callMsg}</div>}
          </div>
        </div>

        {/* VOCI */}
        <div style={styles.col}>
          <h2 style={styles.colTitle}>
            Voci Cartesia ({filteredVoices.length}/{voices.length})
          </h2>
          <div style={styles.filterRow}>
            <input
              type="text"
              placeholder="Cerca per nome, accento, genere..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={styles.input}
            />
            <label style={styles.checkLabel}>
              <input
                type="checkbox"
                checked={showCustomOnly}
                onChange={(e) => setShowCustomOnly(e.target.checked)}
              />
              Solo voci clonate
            </label>
          </div>

          <div style={styles.voiceList}>
            {filteredVoices.map((v) => {
              const isCurrent = selectedAgent && v.voice_id === selectedAgent.voice_id;
              const isPreviewing = previewingId === v.voice_id;
              return (
                <div
                  key={v.voice_id}
                  style={{
                    ...styles.voiceCard,
                    ...(isCurrent ? styles.voiceCardCurrent : {}),
                    ...(v.is_custom ? styles.voiceCardCustom : {}),
                  }}
                >
                  <div style={styles.voiceInfo}>
                    <div style={styles.voiceName}>
                      {v.voice_name}
                      {v.is_custom && <span style={styles.badgeCustom}>CLONATA</span>}
                      {isCurrent && <span style={styles.badgeCurrent}>IN USO</span>}
                    </div>
                    <div style={styles.voiceMeta}>
                      <code style={styles.voiceId}>{v.voice_id}</code>
                      {v.accent && <span> · {v.accent}</span>}
                      {v.gender && <span> · {v.gender}</span>}
                      {v.age && <span> · {v.age}</span>}
                    </div>
                  </div>
                  <div style={styles.voiceActions}>
                    <button
                      onClick={() => handlePreview(v)}
                      style={styles.btnIcon}
                      disabled={!v.preview_audio_url}
                      title={v.preview_audio_url ? "Ascolta anteprima" : "Anteprima non disponibile"}
                    >
                      {isPreviewing ? "⏸" : "▶"}
                    </button>
                    <button
                      onClick={() => handleAssignVoice(v)}
                      disabled={saving || isCurrent || !selectedAgent}
                      style={styles.btnAssign}
                    >
                      {isCurrent ? "In uso" : "Assegna"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    padding: "32px",
    minHeight: "100vh",
    background: "linear-gradient(180deg, #0f172a 0%, #1e293b 100%)",
    color: "#f1f5f9",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px", flexWrap: "wrap", gap: "16px" },
  title: { fontSize: "28px", fontWeight: 700, margin: 0, marginBottom: "6px" },
  subtitle: { fontSize: "13px", color: "#94a3b8", margin: 0, maxWidth: "780px", lineHeight: 1.5 },
  grid: { display: "grid", gridTemplateColumns: "minmax(320px, 380px) 1fr", gap: "24px" },
  col: { display: "flex", flexDirection: "column", gap: "16px" },
  colTitle: { fontSize: "14px", textTransform: "uppercase", color: "#64748b", letterSpacing: "0.05em", margin: 0, marginBottom: "8px" },
  list: { display: "flex", flexDirection: "column", gap: "8px", maxHeight: "50vh", overflowY: "auto", paddingRight: "4px" },
  agentCard: {
    background: "#1e293b",
    border: "1px solid #334155",
    borderRadius: "10px",
    padding: "12px 14px",
    cursor: "pointer",
    transition: "all 0.15s",
  },
  agentCardSelected: {
    border: "2px solid #3b82f6",
    background: "#1e3a8a30",
    transform: "translateX(2px)",
  },
  agentName: { fontWeight: 600, fontSize: "15px", marginBottom: "6px" },
  agentMeta: { display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "6px" },
  tag: { fontSize: "11px", background: "#334155", padding: "3px 8px", borderRadius: "12px", color: "#cbd5e1" },
  phoneList: { display: "flex", gap: "6px", flexWrap: "wrap" },
  phoneTag: { fontSize: "11px", background: "#16a34a30", color: "#86efac", padding: "3px 8px", borderRadius: "12px", border: "1px solid #16a34a60" },
  filterRow: { display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" },
  input: {
    background: "#0f172a",
    border: "1px solid #334155",
    borderRadius: "8px",
    padding: "10px 12px",
    color: "#f1f5f9",
    fontSize: "13px",
    flex: 1,
    minWidth: "200px",
  },
  checkLabel: { display: "flex", gap: "6px", alignItems: "center", fontSize: "13px", color: "#cbd5e1", cursor: "pointer" },
  voiceList: { display: "flex", flexDirection: "column", gap: "6px", maxHeight: "75vh", overflowY: "auto", paddingRight: "4px" },
  voiceCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "#1e293b",
    border: "1px solid #334155",
    borderRadius: "8px",
    padding: "10px 14px",
    gap: "12px",
  },
  voiceCardCurrent: { borderColor: "#3b82f6", background: "#1e3a8a20" },
  voiceCardCustom: { borderLeft: "3px solid #f59e0b" },
  voiceInfo: { flex: 1, minWidth: 0 },
  voiceName: { fontSize: "14px", fontWeight: 600, marginBottom: "3px", display: "flex", gap: "8px", alignItems: "center" },
  voiceMeta: { fontSize: "11px", color: "#94a3b8", display: "flex", gap: "4px", alignItems: "center", flexWrap: "wrap" },
  voiceId: { fontSize: "10px", color: "#64748b", background: "#0f172a", padding: "1px 5px", borderRadius: "3px" },
  voiceActions: { display: "flex", gap: "8px", alignItems: "center" },
  btnIcon: {
    background: "#334155",
    border: "none",
    width: "36px",
    height: "36px",
    borderRadius: "8px",
    color: "#f1f5f9",
    fontSize: "14px",
    cursor: "pointer",
  },
  btnAssign: {
    background: "#3b82f6",
    border: "none",
    padding: "8px 14px",
    borderRadius: "8px",
    color: "white",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
  },
  btnPrimary: {
    background: "linear-gradient(135deg, #10b981, #059669)",
    border: "none",
    padding: "12px 18px",
    borderRadius: "10px",
    color: "white",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    width: "100%",
    marginTop: "10px",
  },
  btnSecondary: {
    background: "#334155",
    border: "1px solid #475569",
    padding: "10px 16px",
    borderRadius: "8px",
    color: "#f1f5f9",
    fontSize: "13px",
    cursor: "pointer",
  },
  badgeCustom: { fontSize: "9px", background: "#f59e0b", color: "#000", padding: "2px 6px", borderRadius: "10px", fontWeight: 700 },
  badgeCurrent: { fontSize: "9px", background: "#3b82f6", color: "white", padding: "2px 6px", borderRadius: "10px", fontWeight: 700 },
  errorBox: { background: "#7f1d1d40", border: "1px solid #b91c1c", color: "#fecaca", padding: "10px 14px", borderRadius: "8px", marginBottom: "12px" },
  okBox: { background: "#14532d40", border: "1px solid #16a34a", color: "#86efac", padding: "10px 14px", borderRadius: "8px", marginBottom: "12px" },
  testBox: { background: "#0f172a", border: "1px solid #334155", borderRadius: "12px", padding: "16px", marginTop: "8px" },
  testTitle: { fontSize: "14px", margin: 0, marginBottom: "10px", color: "#f1f5f9" },
  label: { fontSize: "12px", color: "#94a3b8", marginTop: "8px", marginBottom: "4px", display: "block" },
  testHint: { fontSize: "12px", color: "#64748b", marginTop: "8px" },
  testMsg: { marginTop: "8px", fontSize: "12px", color: "#cbd5e1", wordBreak: "break-all" },
};

export default AdminRetell;
